import { createSelector } from "@reduxjs/toolkit";

import type { RootState } from "../store";

const selectProjectsState = (state: RootState) => state.projects;

export const selectActiveCompany = createSelector(
  [selectProjectsState],
  (s) => s.activeCompany
);

export const selectProjects = createSelector(
  [selectProjectsState],
  (s) => s.projects
);

export const selectProjectsLoading = createSelector(
  [selectProjectsState],
  (s) => s.projectsLoading
);

export const selectCurrentProject = createSelector(
  [selectProjectsState],
  (s) => s.currentProject
);

export const selectCurrentProjectLoading = createSelector(
  [selectProjectsState],
  (s) => s.currentProjectLoading
);

export const selectCurrentBoard = createSelector(
  [selectProjectsState],
  (s) => s.currentBoard
);

export const selectBoards = createSelector(
  [selectProjectsState],
  (s) => s.boards
);

export const selectStatuses = createSelector(
  [selectProjectsState],
  (s) => s.statuses
);

export const selectMembers = createSelector(
  [selectProjectsState],
  (s) => s.members
);

export const selectMembersLoading = createSelector(
  [selectProjectsState],
  (s) => s.membersLoading
);

export const selectLabels = createSelector(
  [selectProjectsState],
  (s) => s.labels
);

export const selectCustomFields = createSelector(
  [selectProjectsState],
  (s) => s.customFields
);

export const selectIssuesById = createSelector(
  [selectProjectsState],
  (s) => s.issues
);

export const selectIssuesByProject = (projectId: number | null | undefined) =>
  createSelector([selectProjectsState], (s) => {
    if (!projectId) return [];
    const ids = s.issuesByProject[projectId] || [];
    return ids.map((id) => s.issues[id]).filter(Boolean);
  });

export const selectIssuesLoading = createSelector(
  [selectProjectsState],
  (s) => s.issuesLoading
);

export const selectActiveIssue = createSelector(
  [selectProjectsState],
  (s) => s.activeIssue
);

export const selectActiveIssueLoading = createSelector(
  [selectProjectsState],
  (s) => s.activeIssueLoading
);

export const selectSprints = createSelector(
  [selectProjectsState],
  (s) => s.sprints
);

export const selectActiveSprint = createSelector(
  [selectProjectsState],
  (s) => s.sprints.find((sp) => sp.state === "ACTIVE") || null
);

export const selectSprintsLoading = createSelector(
  [selectProjectsState],
  (s) => s.sprintsLoading
);

export const selectActivity = createSelector(
  [selectProjectsState],
  (s) => s.activity
);

export const selectActivityLoading = createSelector(
  [selectProjectsState],
  (s) => s.activityLoading
);

export const selectBoardFilters = createSelector(
  [selectProjectsState],
  (s) => s.boardFilters
);

export const selectBacklogFilters = createSelector(
  [selectProjectsState],
  (s) => s.backlogFilters
);

export const selectProjectsError = createSelector(
  [selectProjectsState],
  (s) => s.error
);

export const selectComponents = createSelector(
  [selectProjectsState],
  (s) => s.components
);

export const selectVersions = createSelector(
  [selectProjectsState],
  (s) => s.versions
);

export const selectResolutions = createSelector(
  [selectProjectsState],
  (s) => s.resolutions
);

export const selectNotifications = createSelector(
  [selectProjectsState],
  (s) => s.notifications
);

export const selectNotificationsLoading = createSelector(
  [selectProjectsState],
  (s) => s.notificationsLoading
);

export const selectUnreadNotificationCount = createSelector(
  [selectProjectsState],
  (s) => s.unreadNotificationCount
);

export const selectSavedFilters = createSelector(
  [selectProjectsState],
  (s) => s.savedFilters
);

/* ─── R2: MSP Planning Layer ─── */

export const selectDependencies = createSelector(
  [selectProjectsState],
  (s) => s.dependencies
);

export const selectDependenciesLoading = createSelector(
  [selectProjectsState],
  (s) => s.dependenciesLoading
);

export const selectBaselines = createSelector(
  [selectProjectsState],
  (s) => s.baselines
);

export const selectBaselinesLoading = createSelector(
  [selectProjectsState],
  (s) => s.baselinesLoading
);

export const selectActiveBaseline = createSelector(
  [selectProjectsState],
  (s) => s.activeBaseline
);

export const selectScheduleResult = createSelector(
  [selectProjectsState],
  (s) => s.scheduleResult
);

export const selectCriticalPath = createSelector(
  [selectProjectsState],
  (s) => s.criticalPath
);

export const selectSchedulingLoading = createSelector(
  [selectProjectsState],
  (s) => s.schedulingLoading
);

export const selectCapacity = createSelector(
  [selectProjectsState],
  (s) => s.capacity
);

export const selectCapacityLoading = createSelector(
  [selectProjectsState],
  (s) => s.capacityLoading
);
